import { findCountryName } from "@redotech/locale/countries";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { supplierIdFormat } from "../ids";
import { Provider } from "../order";

const LocationForMoveSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  movable: z.boolean().nullish(),
  message: z.string().nullish(),
  unavailableLineItemsCount: z.number().nullish(),
  selectedByFO: z.boolean().nullish(),
  redoOnly: z.boolean().nullish(),
});
export const LocationForMoveSchema: z.ZodType<LocationForMove> =
  LocationForMoveSchema_;
export interface LocationForMove
  extends z.infer<typeof LocationForMoveSchema_> {}

const ShopifyLocationAddressSchema = z.object({
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  city: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  phone: z.string().nullish(),
  province: z.string().nullish(),
  provinceCode: z.string().nullish(),
  zip: z.string().nullish(),
  company: z.string().nullish(),
});

export const ShopifyFulfillmentServiceSchema = z.object({
  id: z.string(),
  type: z.string().nullish(),
  serviceName: z.string().nullish(),
});

export const ShopifyLocationSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: ShopifyLocationAddressSchema,
  addressVerified: z.boolean(),
  createdAt: z.string(),
  fulfillmentService: ShopifyFulfillmentServiceSchema.nullish(),
  fulfillsOnlineOrders: z.boolean(),
  isActive: z.boolean(),
  shipsInventory: z.boolean(),
  updatedAt: z.string(),
});

export type ShopifyFulfillmentLocation = z.infer<typeof ShopifyLocationSchema>;

export const RedoFulfillmentLocationSchema = ShopifyLocationSchema.omit({
  id: true,
}).extend({
  _id: zExt.objectId().nullish(),
  provider: createEnum(Provider, { enumName: "Provider" }),
  externalId: z.string(),
  teamId: z.string(),
  providerCreatedAt: z.date(),
  providerUpdatedAt: z.date(),
  updatedAt: z.date(),
  createdAt: z.date(),
  canonicalLocationExternalId: z.string().nullish(),
  shippingName: z.string().nullish(),
  supplierId: supplierIdFormat.schema.nullish(),
});

export type RedoFulfillmentLocation = z.infer<
  typeof RedoFulfillmentLocationSchema
>;

export interface IRedoFulfillmentLocation
  extends z.ZodType<RedoFulfillmentLocation> {}

export const CreateRedoFulfillmentLocationSchema =
  RedoFulfillmentLocationSchema.omit({
    _id: true,
    updatedAt: true,
    createdAt: true,
  });

export type CreateRedoFulfillmentLocation = z.infer<
  typeof CreateRedoFulfillmentLocationSchema
>;

export function mapShopifyFulfillmentLocation(
  location: ShopifyFulfillmentLocation,
  teamId: string,
): CreateRedoFulfillmentLocation {
  const data = {
    ...location,
    provider: Provider.SHOPIFY,
    externalId: location.id,
    teamId,
    providerCreatedAt: new Date(location.createdAt),
    providerUpdatedAt: new Date(location.updatedAt),
    address: location.address
      ? {
          ...location.address,
          // Shopify often omits the full country name; derive it from countryCode
          // so the `country` column is never blank on new records.
          country:
            location.address.country ??
            (location.address.countryCode
              ? (findCountryName(location.address.countryCode) ?? null)
              : null),
        }
      : location.address,
  };
  return CreateRedoFulfillmentLocationSchema.parse(data);
}
